/* eslint-disable jira/filename-case */

// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	type Action,
	type BoundActions,
	type HookActionsFunction,
	type HookFunction,
	type SubscriberComponent,
	createActionsHook,
	createHook,
	createStore,
	createSubscriber,
} from '@atlassian/react-sweet-state';
import type { FeedbackCollectorProps, Field, RenderCustomContentType } from '../types.tsx';
import type { State } from './types.tsx';

export const initialState: State = {
	isFeedbackCollectorOpen: false,
	entrypointId: undefined,
	email: undefined,
	additionalFields: undefined,
	feedbackTitle: undefined,
	feedbackContext: undefined,
	feedbackCollectorId: undefined,
	// default value of this prop is true so making sure inititally it is true
	showTypeField: true,
	submitButtonLabel: undefined,
	canBeContactedLabel: undefined,
	canBeContactedLink: undefined,
	enrolInResearchLink: undefined,
	summaryPlaceholder: undefined,
	feedbackGroupLabels: undefined,
	onClose: undefined,
	onCancel: undefined,
	feedbackTitleDetails: undefined,
	triggerRef: undefined,
	dialogRef: undefined,
	renderCustomContent: undefined,
	customFeedbackOptions: [],
};

export const actions = {
	openFeedbackCollector:
		(feedbackCollectorProps: FeedbackCollectorProps): Action<State> =>
		({ setState, dispatch }) => {
			// Reset to initial state to clear any leftover state
			dispatch(actions.resetFeedbackCollectorState());

			setState({
				...feedbackCollectorProps,
				isFeedbackCollectorOpen: true,
			});
		},
	closeFeedbackCollector:
		(): Action<State> =>
		({ getState, setState }) => {
			const { triggerRef } = getState();
			if (triggerRef) {
				// Unfortunately we need a setTimeout here to delay the execution cycle because modal is yet to be closed
				setTimeout(() => triggerRef.current?.focus(), 50);
			}
			setState({
				isFeedbackCollectorOpen: false,
				triggerRef: undefined,
				dialogRef: undefined,
			});
		},
	resetFeedbackCollectorState:
		(): Action<State> =>
		({ setState }) => {
			setState({ ...initialState });
		},
	updateCustomRenderComponent:
		(updatedCustomRenderContent: RenderCustomContentType): Action<State> =>
		({ setState }) => {
			setState({
				renderCustomContent: updatedCustomRenderContent,
			});
		},
	updateAdditionalFields:
		(updatedAdditionalFields: Field[] | undefined): Action<State> =>
		({ setState }) => {
			setState({
				additionalFields: updatedAdditionalFields,
			});
		},
	updateFeedbackContext:
		(updatedFeedbackContext: FeedbackCollectorProps['feedbackContext']): Action<State> =>
		({ setState }) => {
			setState({
				feedbackContext: updatedFeedbackContext,
			});
		},
} as const;

type Actions = typeof actions;

const Store = createStore<State, Actions>({
	name: 'feedback-collector',
	initialState,
	actions,
});

export const useFeedbackCollector: HookFunction<
	State,
	BoundActions<
		State,
		{
			readonly openFeedbackCollector: (
				feedbackCollectorProps: FeedbackCollectorProps,
			) => Action<State>;
			readonly closeFeedbackCollector: () => Action<State>;
			readonly resetFeedbackCollectorState: () => Action<State>;
			readonly updateCustomRenderComponent: (
				updatedCustomRenderContent: RenderCustomContentType,
			) => Action<State>;
			readonly updateAdditionalFields: (
				updatedAdditionalFields: Field[] | undefined,
			) => Action<State>;
			readonly updateFeedbackContext: (
				updatedFeedbackContext: FeedbackCollectorProps['feedbackContext'],
			) => Action<State>;
		}
	>,
	void
> = createHook(Store);
// @ts-expect-error - TS2322 - Type 'HookActionsFunction<BoundActions<State, { readonly openFeedbackCollector: (feedbackCollectorProps: FeedbackCollectorProps) => Action<State, void, void | Promise<void>>; readonly closeFeedbackCollector: () => Action<...>; readonly resetFeedbackCollectorState: () => Action<...>; }>>' is not assignable to type 'HookActionsFunction<{ readonly openFeedbackCollector: (feedbackCollectorProps: FeedbackCollectorProps) => Action<State, void, void | Promise<void>>; readonly closeFeedbackCollector: () => Action<...>; readonly resetFeedbackCollectorState: () => Action<...>; }>'.
export const useFeedbackCollectorActions: HookActionsFunction<Actions> = createActionsHook(Store);
export const FeedbackActionsSubscriber: SubscriberComponent<null, Actions> = createSubscriber(
	Store,
	{
		selector: null,
	},
);
